import { Color } from "@repo/app-types/constants/colors";
import type { ZoomContactCenterQueue } from "@repo/app-types/entities/integration";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { Size } from "@repo/ui/helpers/helpers";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Input } from "@repo/ui/Inputs/input";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { Pill, type PillColor } from "@repo/ui/Pills/pill";
import { SimpleRadioGroup } from "@repo/ui/Radio/simpleRadioGroup";
import { SimpleRadioOption } from "@repo/ui/Radio/simpleRadioOption";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import {
  SegmentedControl,
  type SegmentedControlOption,
} from "@repo/ui/toggleButton/segmentedControl";
import { IconSearch } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";

/**
 * A queue Zoom returned (`isListed`), or a selected id it didn't: one deleted
 * in Zoom, or, when the list is capped, one past the cap.
 */
interface QueueRow extends ZoomContactCenterQueue {
  isListed: boolean;
}

/** `all`: `zoom_queue_ids` unset, every queue. `selected`: exactly a list. */
type QueueMode = "all" | "selected";

type QueueStatus = "importing" | "notImporting" | "notInZoom";

const QUEUE_STATUSES: Record<QueueStatus, { label: string; color: PillColor }> =
  {
    importing: { label: "Importing", color: Color.Green },
    notImporting: { label: "Not importing", color: Color.Red },
    notInZoom: { label: "Not in Zoom", color: Color.Amber },
  };

/**
 * Selected ids Zoom didn't return first, by id, so they aren't lost at the
 * bottom of a long table; then Zoom's queues, which the API sorts by name.
 */
function buildQueueRows(
  zoomQueues: readonly ZoomContactCenterQueue[],
  storedIds: readonly string[],
): QueueRow[] {
  const listedIds = new Set(zoomQueues.map((queue) => queue.id));
  return [
    ...storedIds
      .filter((id) => !listedIds.has(id))
      .sort((a, b) => a.localeCompare(b))
      .map((id) => ({ id, name: null, isListed: false })),
    ...zoomQueues.map((queue) => ({ ...queue, isListed: true })),
  ];
}

/**
 * A selected id Zoom didn't return is only known to be gone when the list is
 * complete; past the cap, the poll still imports it.
 */
function computeQueueStatus(
  row: QueueRow,
  selectedIds: ReadonlySet<string>,
  isTruncated: boolean,
): QueueStatus {
  if (!row.isListed && !isTruncated) {
    return "notInZoom";
  }
  return selectedIds.has(row.id) ? "importing" : "notImporting";
}

export function formatQueueCount(count: number): string {
  return `${count.toLocaleString()} ${count === 1 ? "queue" : "queues"}`;
}

/** Ignores case and whitespace. */
function normalizeSearchText(text: string): string {
  return text.toLowerCase().replace(/\s+/g, "");
}

function checkQueueSearchMatch(
  row: QueueRow,
  normalizedQuery: string,
): boolean {
  return [row.name ?? "", row.id].some((field) =>
    normalizeSearchText(field).includes(normalizedQuery),
  );
}

export interface ZoomContactCenterQueuePickerProps {
  /** Zoom's live queue list, sorted by name. */
  zoomQueues: readonly ZoomContactCenterQueue[];
  /** Zoom has more queues than one listing reads. */
  isTruncated: boolean;
  /** The selection: `null` imports every queue, including ones added later. */
  queueIds: readonly string[] | null;
  /** Never called with an empty list, which would mean every queue. */
  onChange: (queueIds: readonly string[] | null) => void;
  isDisabled: boolean;
  isSaving?: boolean;
  /** Said when the last importing queue can't be cleared. */
  minimumSelectionHint: string;
}

/**
 * Import from every Zoom Contact Center queue or only the selected ones, chosen
 * from Zoom's live queue list. Controlled: the Settings tab saves each change
 * as it is made, while connecting holds the choice until the connection is
 * saved.
 */
export const ZoomContactCenterQueuePicker: FC<
  ZoomContactCenterQueuePickerProps
> = (props) => {
  const {
    zoomQueues,
    isTruncated,
    queueIds,
    onChange,
    isDisabled,
    isSaving = false,
    minimumSelectionHint,
  } = props;
  const [isAllQueuesConfirmationOpen, setIsAllQueuesConfirmationOpen] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // A status filter's rows are fixed when it is picked, so a queue selected
  // under "Not importing" stays in view, now Importing.
  const [statusFilter, setStatusFilter] = useState<{
    status: QueueStatus;
    ids: ReadonlySet<string>;
  } | null>(null);

  const storedIds = queueIds ?? [];
  const mode: QueueMode = storedIds.length === 0 ? "all" : "selected";
  const rows = buildQueueRows(zoomQueues, storedIds);
  const zoomQueueIds = zoomQueues.map((queue) => queue.id);
  const selectedIds = new Set(mode === "all" ? zoomQueueIds : storedIds);
  const statusById = new Map(
    rows.map((row) => [
      row.id,
      computeQueueStatus(row, selectedIds, isTruncated),
    ]),
  );
  const statusCounts = { importing: 0, notImporting: 0, notInZoom: 0 };
  for (const status of statusById.values()) {
    statusCounts[status]++;
  }

  // The status filter only applies when choosing queues: in "all" mode every
  // queue imports.
  const activeStatusFilter = mode === "all" ? null : statusFilter;
  const normalizedQuery = normalizeSearchText(searchQuery);
  const visibleRows = rows.filter(
    (row) =>
      (activeStatusFilter?.ids.has(row.id) ?? true) &&
      checkQueueSearchMatch(row, normalizedQuery),
  );
  const visibleZoomQueueIds = visibleRows
    .filter((row) => row.isListed)
    .map((row) => row.id);
  const visibleSelectedCount = visibleZoomQueueIds.filter((id) =>
    selectedIds.has(id),
  ).length;
  const isEveryVisibleSelected =
    visibleZoomQueueIds.length > 0 &&
    visibleSelectedCount === visibleZoomQueueIds.length;

  // An empty list would mean every queue, so the last selected queue can't be
  // cleared, nor the last one still importing (only gone queues would be
  // left).
  const checkSelectionLocked = (row: QueueRow) =>
    selectedIds.has(row.id) &&
    (selectedIds.size === 1 ||
      (statusById.get(row.id) === "importing" && statusCounts.importing === 1));

  // Always a list, even with every queue selected, so a queue added in Zoom
  // later starts out not imported.
  const changeSelection = (nextSelectedIds: ReadonlySet<string>) =>
    onChange(
      rows.filter((row) => nextSelectedIds.has(row.id)).map((row) => row.id),
    );

  const toggleQueue = (id: string, isSelected: boolean) => {
    const next = new Set(selectedIds);
    if (isSelected) {
      next.add(id);
    } else {
      next.delete(id);
    }
    changeSelection(next);
  };

  // Selects the Zoom queues shown, keeping every other selection.
  const selectVisibleQueues = () =>
    changeSelection(new Set([...selectedIds, ...visibleZoomQueueIds]));

  const changeMode = (nextMode: string) => {
    if (nextMode === "all") {
      // It drops the selection for good, so it is confirmed first.
      setIsAllQueuesConfirmationOpen(true);
      return;
    }
    // Starts from every Zoom queue, so switching stops nothing.
    setStatusFilter(null);
    onChange(zoomQueueIds);
  };

  const confirmAllQueues = () => {
    setIsAllQueuesConfirmationOpen(false);
    setStatusFilter(null);
    onChange(null);
  };
  const changeStatusFilter = (status: QueueStatus | "all") => {
    setStatusFilter(
      status === "all"
        ? null
        : {
            status,
            ids: new Set(
              rows
                .filter((row) => statusById.get(row.id) === status)
                .map((row) => row.id),
            ),
          },
    );
  };

  const statusOptions: SegmentedControlOption<QueueStatus | "all">[] = [
    { id: "all", label: `All (${rows.length.toLocaleString()})` },
    ...(Object.keys(QUEUE_STATUSES) as QueueStatus[])
      .filter((status) => status !== "notInZoom" || statusCounts.notInZoom > 0)
      .map((status) => ({
        id: status,
        label: `${QUEUE_STATUSES[status].label} (${statusCounts[status].toLocaleString()})`,
      })),
  ];

  const columns: EntityTableColumnDefinition<QueueRow>[] = [
    {
      id: "selected",
      className: "w-12",
      header: (
        // Only ever selects: clearing everything would mean every queue.
        <Checkbox
          accessibleLabel="Import from every queue shown"
          isChecked={isEveryVisibleSelected}
          isDisabled={isDisabled || mode === "all" || isEveryVisibleSelected}
          isIndeterminate={!isEveryVisibleSelected && visibleSelectedCount > 0}
          onChange={selectVisibleQueues}
        />
      ),
      renderCell: (row) => (
        <Checkbox
          accessibleLabel={`Import from ${row.name ?? row.id}`}
          isChecked={selectedIds.has(row.id)}
          isDisabled={isDisabled || mode === "all" || checkSelectionLocked(row)}
          onChange={(isSelected) => toggleQueue(row.id, isSelected)}
        />
      ),
    },
    {
      id: "name",
      header: "Queue",
      isRowHeader: true,
      renderCell: (row) => (
        <span
          className={twMerge(
            "block max-w-xs truncate",
            row.name ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {row.name ??
            (row.isListed
              ? "Unnamed queue"
              : isTruncated
                ? "Not in the list shown"
                : "Unknown queue")}
        </span>
      ),
    },
    {
      id: "queueId",
      header: "Queue ID",
      renderCell: (row) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.id}
        </span>
      ),
    },
    {
      id: "status",
      className: "text-right",
      header: "Status",
      renderCell: (row) => {
        const status = QUEUE_STATUSES[statusById.get(row.id) ?? "notInZoom"];
        return (
          <div className="flex justify-end">
            <Pill color={status.color} label={status.label} size={Size.SMALL} />
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {isTruncated ? (
        <div className="mt-4">
          <InfoBanner
            description={
              mode === "all"
                ? "This Zoom account has more queues than Alpharun can list, so every queue is imported. Contact Alpharun support to import only some queues."
                : "This Zoom account has more queues than Alpharun can list, so some selected queues may be shown without a name. Contact Alpharun support to select a queue that isn't shown."
            }
            title="Not every queue is listed"
            variant={SimpleModalVariant.Standard}
          />
        </div>
      ) : null}

      <div className="mt-4">
        <SimpleRadioGroup onChange={changeMode} value={mode}>
          <SimpleRadioOption isDisabled={isDisabled} value="all">
            All queues, including queues added in Zoom later
          </SimpleRadioOption>
          {/* A capped list can't show every queue a selection would
              drop, so choosing queues is off while the list is capped. */}
          <SimpleRadioOption
            isDisabled={isDisabled || zoomQueueIds.length === 0 || isTruncated}
            value="selected"
          >
            Only the queues selected below
          </SimpleRadioOption>
        </SimpleRadioGroup>
      </div>
      <div className="mt-4 flex items-center justify-between gap-4 text-sm text-muted-foreground">
        <p>
          {mode === "all"
            ? `Importing from every queue (${formatQueueCount(zoomQueues.length)}).`
            : isTruncated
              ? `Importing from ${formatQueueCount(statusCounts.importing)}.`
              : `Importing from ${statusCounts.importing.toLocaleString()} of ${formatQueueCount(zoomQueues.length)}${
                  statusCounts.notInZoom > 0
                    ? `, plus ${statusCounts.notInZoom.toLocaleString()} selected ${statusCounts.notInZoom === 1 ? "queue that's" : "queues that are"} no longer in Zoom`
                    : ""
                }. Queues added in Zoom later aren't imported until you select them.`}
        </p>
        <p aria-live="polite">{isSaving ? "Saving…" : null}</p>
      </div>
      {rows.length > 0 ? (
        <>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="w-full max-w-xs">
              <Input
                accessibleLabel="Search queues"
                leftIcon={<IconSearch className="size-4 text-gray-400" />}
                maxLength={100}
                onChange={(event) => setSearchQuery(event.target.value)}
                onClear={() => setSearchQuery("")}
                placeholder="Search by name or ID"
                value={searchQuery}
              />
            </div>
            {mode === "all" ? null : (
              <SegmentedControl
                aria-label="Queue status"
                onChange={changeStatusFilter}
                options={statusOptions}
                size={Size.SMALL}
                value={activeStatusFilter?.status ?? "all"}
              />
            )}
          </div>
          {/* Scrolls in its own frame: an account can have hundreds of
              queues, and the rest of the tab sits below. */}
          <div className="mt-2 max-h-[28rem] overflow-y-auto">
            <EntityTable
              aria-label="Zoom Contact Center queues"
              columns={columns}
              emptyMessage="No queues match."
              entities={visibleRows}
              size={Size.SMALL}
            />
          </div>
        </>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">
          Zoom lists no queues on this account.
        </p>
      )}
      {mode === "selected" &&
      (selectedIds.size === 1 || statusCounts.importing === 1) ? (
        <p className="mt-2 text-sm text-muted-foreground">
          {minimumSelectionHint}
        </p>
      ) : null}

      <SimpleModal
        confirmButtonText="Import all queues"
        isOpen={isAllQueuesConfirmationOpen}
        onCancel={() => setIsAllQueuesConfirmationOpen(false)}
        onConfirm={confirmAllQueues}
        subtitle="Every queue on this Zoom account will be imported, including queues added later. Your selection isn't kept: switching back starts with every queue selected."
        title="Import from every queue?"
        variant={SimpleModalVariant.Warning}
      />
    </div>
  );
};
